import { Auth } from 'aws-amplify'
import router from '@/router'
import api from '@/libs/api'

const AUTH_ERROR = 'AUTH_ERROR'
const AUTH_LOADING = 'AUTH_LOADING'
const SIGN_OUT = 'SIGN_OUT'
const CLEAR_STATE = 'CLEAR_STATE'
const SET_SESSION = 'SET_SESSION'
const SIGN_IN_SUCCESS = 'SIGN_IN_SUCCESS'

const NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED'

const forceNewPassword = ({ challengeName }) =>
  challengeName === NEW_PASSWORD_REQUIRED

const resetState = () => ({
  session: null,
  loading: false,
  isLogged: false,
  cognitoUser: null,
  cognitoInfo: null,
  cognitoError: null
})

const AuthCog = (customer) =>
  Auth.configure({
    region: process.env.VUE_APP_COGNITO_REGION,
    userPoolId: process.env.VUE_APP_COGNITO_POOL_ID,
    userPoolWebClientId: process.env.VUE_APP_COGNITO_CLIENT_ID,
    clientMetadata: { customer }
  })

const provisionParams = (payload) => ({
  areaCode: payload['custom:areaCode'],
  authorization: {
    type: 'Bearer',
    token: payload['custom:authorization']
  },
  host: payload['custom:host'],
  password: payload['custom:extensionPwd'],
  extension: payload['custom:extension']
})

const state = resetState()
const getters = {
  isLogged: (state) => {
    return state.isLogged
  },

  isLoading: (state) => {
    return state.loading
  },

  cognitoError: (state) => {
    const translateError = {
      'User does not exist.': 'Usuário Web não existe.',
      'Incorrect username or password.': 'Matrícula ou senha incorretos.',
      'Password attempts exceeded': 'Tentativas de senha excedidas.',
      'User is not confirmed.': 'Usuário não está confirmado.',
      'Password reset required for the user.': 'Redefinição de senha necessária para o usuário.',
      'NotAuthorizedException: Password attempts exceeded': 'Tentativas de senha excedidas.',
      'PreAuthentication failed with error Error: Agente inativo.': 'Agente inativo.'
    }

    return translateError[state.cognitoError]
  }
}

const mutations = {
  [AUTH_LOADING]: (state) => {
    Object.assign(state, {
      loading: true,
      cognitoInfo: null,
      cognitoError: null
    })
  },

  [AUTH_ERROR]: (state, error) => {
    Object.assign(state, {
      loading: false,
      cognitoError: error.message
    })
  },

  [SIGN_IN_SUCCESS]: (state, session) => {
    Object.assign(state, {
      session,
      loading: false,
      isLogged: true,
      cognitoUser: null
    })
  },

  [SET_SESSION]: (state, session) => {
    Object.assign(state, {
      session,
      loading: false,
      cognitoUser: null
    })
  },

  [NEW_PASSWORD_REQUIRED]: (state, cognitoUser) => {
    Object.assign(state, {
      loading: false,
      cognitoUser,
      cognitoInfo: 'NEW_PASSWORD_REQUIRED'
    })
    router.push({ name: 'ROUTE_AUTH_PASSWORD_DEFINE' }).catch(() => ({}))
  },

  [SIGN_OUT]: (state) => {
    Object.assign(state, resetState())
  },

  [CLEAR_STATE]: (state) => {
    Object.assign(state, resetState())
  }
}

const actions = {
  async checkSession ({ commit, rootGetters }) {
    commit(AUTH_LOADING)
    try {
      const customer = rootGetters['settingsModule/customer']

      AuthCog(customer)

      if (!Auth || !Auth.currentSession) {
        return router.push({ name: 'ROUTE_AUTH' }).catch(() => ({}))
      }

      const session = await Auth.currentSession()
      commit(SET_SESSION, session)
      const { payload } = session.getIdToken()
      const provision = provisionParams(payload)
      provision.customer = customer
      provision.agent = payload['cognito:username'].split(customer)[1]

      commit(SIGN_IN_SUCCESS, { provision })
    } catch (error) {
      commit(AUTH_ERROR, error)
    }
  },

  async login ({ commit, dispatch, rootGetters }, { agent, password, redirect }) {
    commit(AUTH_LOADING)
    try {
      const customer = rootGetters['settingsModule/customer']

      AuthCog(customer)

      const userName = `${customer}${agent}`
      const cognitoUser = await Auth.signIn(userName, password)

      if (forceNewPassword(cognitoUser)) {
        return commit(NEW_PASSWORD_REQUIRED, cognitoUser)
      }

      const session = await Auth.currentSession()

      dispatch('agentModule/setConnectSip', { loadType: 'session' }, { root: true })

      commit(SET_SESSION, session)
      const { payload } = session.getIdToken()
      const provision = provisionParams(payload)
      provision.agent = agent
      provision.customer = customer

      commit(SIGN_IN_SUCCESS, { provision })
    } catch (error) {
      commit(AUTH_ERROR, error)
    }
  },

  async logout ({ commit, dispatch }) {
    router.push({ name: 'ROUTE_AUTH' }).catch(() => null)
    dispatch('agentModule/setConnectSip', { loading: true, loadType: 'logout' }, { root: true })
    Auth.signOut()

    dispatch('sipModule/disconnect', null, { root: true })

    await new Promise((resolve) => {
      setTimeout(() => {
        dispatch('agentModule/setConnectSip', { loading: false, loadType: '' }, { root: true })
        dispatch('attendanceModule/clearTextAttendances', null, { root: true })
        dispatch('agentModule/clearScheduleLogout', null, { root: true })
        dispatch('calendarModule/clearHistory', null, { root: true })
        dispatch('agentModule/updateAgent', { logged: false, loggedAt: 0 }, { root: true })
        dispatch('agentModule/clearSession', null, { root: true })
        dispatch('clearState')
        resolve()
      }, 1000)
    })
  },

  async clearState ({ commit }) {
    commit(CLEAR_STATE)
  },

  async definePassword ({ dispatch, commit, state, rootGetters }, password) {
    commit(AUTH_LOADING)

    try {
      const userName = state.cognitoUser.username
      const customer = rootGetters['settingsModule/customer']
      await Auth.completeNewPassword(state.cognitoUser, password)
      dispatch('login', { userName, password, customer })
    } catch (error) {
      console.log({ error })
      commit(AUTH_ERROR, error)
    }
  }
}

const middlewares = {
  'authModule/NEW_PASSWORD_REQUIRED': () => {
    router.push({ name: 'ROUTE_AUTH_PASSWORD_DEFINE' }).catch(() => ({}))
  },
  'authModule/SIGN_IN_SUCCESS': async (ctx, { payload: { provision } }) => {
    ctx.dispatch('agentModule/setProvision', provision, { root: true })
    await api.setConfiguration(provision.customer, provision.authorization)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  middlewares
}
